import { useEffect, useRef, useCallback } from 'react'
import confetti, { Shape } from 'canvas-confetti'
import { TConfettiProps } from '../constants/types'

function randomInRange(min: number, max: number) {
  return Math.random() * (max - min) + min
}

export function useConfetti({
  particleCount = 1,
  startVelocity = 0,
  duration = 15000,
  origin = {
    x: null ,
    y: null,
  },
  angle = 0,
  spread = 0,
  interval = 500,
  colors = ['#ffffff'],
  shapes = ['circle'] as Shape[],
  gravity = randomInRange(0.4, 0.6),
  scalar = randomInRange(2, 5),
  drift = randomInRange(-0.4, 0.4),
  autoStart = false,
}: TConfettiProps = {}) {
  const rafRef = useRef<number | null>(null)
  const endRef = useRef(0)
  const lastEmitRef = useRef(0)
  const skewRef = useRef(1)

  const stop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const fire = useCallback(() => {
    stop()

    endRef.current = Date.now() + duration
    lastEmitRef.current = 0
    skewRef.current = 1

    function frame() {
      const now = Date.now()
      const timeLeft = endRef.current - now

      if (timeLeft <= 0) {
        stop()
        return
      }

      rafRef.current = requestAnimationFrame(frame)

      // 🔥 stable interval-based emission
      if (now - lastEmitRef.current < interval) return
      lastEmitRef.current = now

      skewRef.current = Math.max(0.8, skewRef.current - 0.001)

      confetti({
        particleCount,
        startVelocity,
        ticks: Math.max(200, 500 * (timeLeft / duration)),
        origin: {
          x: origin.x !== null ? origin.x : Math.random(),
          y: origin.y !== null ? origin.y : Math.random() * skewRef.current - 0.2,
        },
        angle,
        spread,
        colors: [
          colors[Math.floor(Math.random() * colors.length)]
        ],
        shapes,
        gravity,
        scalar,
        drift,
      })
    }

    rafRef.current = requestAnimationFrame(frame)
  }, [duration, interval, colors, shapes, stop])

  useEffect(() => {
    if (autoStart) fire()
    return stop
  }, [autoStart, fire, stop])

  return { fire, stop }
}